import {useState} from 'react';
import {ArticleFileManager} from '../helpers/article-file-manager';
import {FileUploadProgress, UploadedFile} from '../models/interfaces/uploaded-file.interface';
import {useLoading} from '../../core/hooks/use-loading.hook';
import {LoadingKeys} from '../../core/models/enums/loading-keys.enum';
import {useArticleApi} from "../../core/hooks/use-article-api";
import FileHelper from "@utils/file-helper";

export const useArticleFileUpload = () => {
    const {uploadTempFile} = useArticleApi();
    const loadingKey = LoadingKeys.USE_FILE_UPLOAD;
    const {startLoading, stopLoading} = useLoading();
    const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
    const [error, setError] = useState<string | null>(null);

    const uploadFile = async (file: File): Promise<UploadedFile> => {
        const fileId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Dodaj do listy uploadów
        setUploadProgress(prev => [...prev, {
            fileId,
            progress: 0,
            status: 'uploading'
        }]);

        startLoading([loadingKey, 'uploadFile']);
        setError(null);

        try {
            // Upload do backendu
            const response = await uploadTempFile(file);

            // Stwórz obiekt UploadedFile
            const uploadedFile: UploadedFile = {
                id: response.id,
                name: response.name,
                url: response.url,
                sizeInBytes: response.sizeInBytes,
                uploadedAt: new Date().toLocaleString(),
                extension: FileHelper.getFileExtension(file)!,
                isUsedInContent: false
            };

            // Zapisz w localStorage
            ArticleFileManager.addFile(uploadedFile);

            // Aktualizuj progress
            setUploadProgress(prev => prev.map(p =>
                p.fileId === fileId
                    ? {...p, progress: 100, status: 'completed'}
                    : p
            ));

            return uploadedFile;
        } catch (err: any) {
            setError(err.message);

            // Aktualizuj progress z błędem
            setUploadProgress(prev => prev.map(p =>
                p.fileId === fileId
                    ? {...p, status: 'error', error: err.message}
                    : p
            ));

            throw err;
        } finally {
            stopLoading([loadingKey, 'uploadFile']);
        }
    };

    const markFileAsUnused = (fileId: string): void => {
        ArticleFileManager.markFileAsUnused(fileId);
    };

    const syncUsedFilesFromContent = (content: string): void => {
        ArticleFileManager.syncUsedFilesFromContent(content);
    };

    const clearUploadProgress = (): void => {
        setUploadProgress([]);
    };

    const getAllFiles = (): UploadedFile[] => {
        return ArticleFileManager.getAllFiles();
    };

    const getUsedFiles = (): string[] => {
        return ArticleFileManager.getUsedFiles();
    };

    return {
        uploadFile,
        markFileAsUnused,
        syncUsedFilesFromContent,
        clearUploadProgress,
        getAllFiles,
        getUsedFiles,
        uploadProgress,
        error,
        setError
    };
};
